import React, {
    forwardRef,
    useCallback,
    useEffect,
    useImperativeHandle,
    useLayoutEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { getter, DataResult, CompositeFilterDescriptor, FilterDescriptor, process } from '@progress/kendo-data-query';
import { DragAndDrop } from '@progress/kendo-react-common';
import { SelectDescriptor } from '@progress/kendo-react-data-tools';
import {
    getSelectedState,
    Grid as KendoGrid,
    GridColumnReorderEvent,
    GridColumnResizeEvent,
    GridColumnsStateChangeEvent,
    GridColumnState,
    GridCustomRowProps,
    GridFilterChangeEvent,
    GridHandle,
    GridHeaderSelectionChangeEvent,
    GridNoRecords,
    GridPageChangeEvent,
    GridSelectionChangeEvent,
    GridSortChangeEvent,
} from '@progress/kendo-react-grid';
import { CardHandle } from '@progress/kendo-react-layout';
import cn from 'clsx';
import { debounce } from 'lodash';
import Loader, { LoaderPosition } from './grid-loader';
import './style.scss';
import ConfirmationDialog from '@components-clean/core/confirmation-dialog';
import useGridCustomCell from '@components-clean/core/grid/custom-cell';
import { DraggableRow, ReorderContext } from '@components-clean/core/grid/draggable-row';
import { FilterOperators } from '@components-clean/core/grid/grid-custom-filter/grid-custom-filter.interface';
import {
    ADJUST_GRID_CONTENT_PADDING,
    calcOperator,
    getMinGridWidth,
    GRID_HEADER_HEIGHT,
    rowMenuActionColumn,
    SELECTED_FIELD,
} from '@components-clean/core/grid/grid.helper';
import {
    GridColumnProps,
    GridProps,
    GridRefExtension,
    GridState,
    ORDER_COLUMN_ID,
    REORDER_COLUMN_ID,
    ROW_MENU_ACTION_COLUMN_ID,
    RowMenuAction,
} from '@components-clean/core/grid/grid.interface';
import NoRecordsMessage from '@components-clean/core/grid/no-records-message';
import { renderGridColumn } from '@components-clean/core/grid/render-grid-column';
import { GRID_COMPONENT_NAME } from '@components-clean/core/grid/shared';
import { updateColumnsFields } from '@components-clean/core/grid/update-columns-fields';
import Loading from '@components-clean/core/loading/loading';
import { useReadOnlyContext } from '@contexts-clean/read-only-context';
import { useUnsavedChangesContext } from '@contexts-clean/unsaved-changes-context';
import { useBoolean, useTranslation } from '@hooks/index';
import { GRID_ROW_TEST_ID_MASTER } from '@utils/automation-test-helpers';

const SCROLL_BAR_WIDTH = 9;

export type CoreGridHandle = {
    unselectRow: (primaryKey: string) => void;
    validateSelection: (newData: unknown[]) => void;
    clearSelectedRows: () => void;
};

export interface CoreGridProps extends GridProps {
    titleIsEmpty?: boolean;
    filterActive?: ReturnType<typeof useBoolean>;
    showAddWarningDialog?: ReturnType<typeof useBoolean>;
    lockedColumnsUpdated?: ReturnType<typeof useBoolean>;
    hiddenColumnsUpdated?: ReturnType<typeof useBoolean>;
    reorderActive?: ReturnType<typeof useBoolean>;
    showReorderConfirmation?: ReturnType<typeof useBoolean>;
    cardRef?: React.RefObject<CardHandle | null>;
}

const Grid = forwardRef<CoreGridHandle, CoreGridProps>(
    (
        {
            titleIsEmpty,
            cardRef,
            title,
            titleGroupByField,
            onTitleChange,
            selectedTitle,
            noTitleMessage,
            resizable = true,
            reorderable = true,
            filterable = true,
            sortable = true,
            scrollable = 'scrollable',
            testDataId,
            columns,
            className,
            data,
            style,
            maxHeight,
            gridViewOptions,
            totalCount,
            exportCallback,
            onColumnReorder,
            clearFilter,
            addCallback,
            dataItemKey,
            select,
            selectedField,
            defaultSelect,
            onSelectionChange,
            onHeaderSelectionChange,
            preSelectionChange,
            selectable,
            toggleReorder,
            saveReorder,
            cancelReorder,
            rowReorder = false,
            orderIndexColumn = 'displayIndex',
            reorderConfirmation,
            defaultSelectedItem,
            canEdit,
            setExportColumns,
            loading,
            rowMenuActions,
            rowMenuActionsWidth,
            filter,
            setReorderIsDirty,
            setReorderIsActive,
            useLoadingOverlay,
            clientGrid,
            scrollGridRight,
            onChipHeaderSelected,
            selectedChipHeader,
            noPadding,
            screenFilterable,
            gridStatusSetup,
            emailCallback,
            bulkUpdateTooltip,
            addRecordTooltip,
            onBulkUpdateClicked,
            isChildGrid,
            clearGridSelectedRows,
            apiGrid,
            showPlaceholder = false,
            onPageChange,
            preventAutoscroll = false,
            onFilterChange,
            onSortChange,
            persistentStorageKey,
            gridKey: propGridKey,
            setGridKey: propSetGridKey,
            tmpReorder: propTmpReorder,
            setTmpReorder: propSetTmpReorder,
            selectedState: propSelectedState,
            setSelectedState: propSetSelectedState,
            currentColumns: propCurrentColumns,
            setCurrentColumns: propSetCurrentColumns,
            dataState: propDataState,
            setDataState: propSetDataState,
            saveTableState: propSaveTableState,
            filterActive: propFilterActive,
            showAddWarningDialog: propShowAddWarningDialog,
            lockedColumnsUpdated: propLockedColumnsUpdated,
            reorderActive: propReorderActive,
            hiddenColumnsUpdated: propHiddenColumnsUpdated,
            selectionDisabledField,
            excludeHiddenRowMenuActions,
            externalColumnsState,
            onColumnsStateChange: onColumnsStateChangeProp,
            onStateChange,
            ...rest
        }: CoreGridProps,
        ref,
    ) => {
        const { setComponentHasChanges } = useUnsavedChangesContext();
        const { t } = useTranslation();
        const { readOnly } = useReadOnlyContext();
        const { HeaderCell, MenuActionCell } = useGridCustomCell();
        const checkBoxSelectionOnly = !!columns.find((col) => col.columnType === 'checkbox');
        const [editWarningCallback, setEditWarningCallback] = useState<() => void>();

        // Use propFilterActive if provided, otherwise use internal state
        const internalFilterActive = useBoolean(false);
        const filterActive = propFilterActive !== undefined ? propFilterActive : internalFilterActive;

        // Use propShowAddWarningDialog if provided, otherwise use internal state
        const internalShowAddWarningDialog = useBoolean(false);
        const showAddWarningDialog =
            propShowAddWarningDialog !== undefined ? propShowAddWarningDialog : internalShowAddWarningDialog;

        // Use propLockedColumnsUpdated if provided, otherwise use internal state
        const internalLockedColumnsUpdated = useBoolean(false);
        const lockedColumnsUpdated =
            propLockedColumnsUpdated !== undefined ? propLockedColumnsUpdated : internalLockedColumnsUpdated;

        // Use propReorderActive if provided, otherwise use internal state
        const internalReorderActive = useBoolean(false);
        const reorderActive = propReorderActive !== undefined ? propReorderActive : internalReorderActive;

        // Use propDataState/propSetDataState if provided, otherwise use internal state
        const [internalDataState, internalSetDataState] = React.useState<GridState>({
            data: [],
            total: Array.isArray(data) ? data.length : data ? data.data.length : 0,
            filter,
            sort: rest.sort ?? [],
        });
        const dataState = propDataState !== undefined ? propDataState : internalDataState;
        const setDataState = propSetDataState !== undefined ? propSetDataState : internalSetDataState;

        // Use propTmpReorder/propSetTmpReorder if provided, otherwise use internal state
        const [internalTmpReorder, internalSetTmpReorder] = useState<unknown[] | DataResult | null>();
        const tmpReorder = propTmpReorder !== undefined ? propTmpReorder : internalTmpReorder;
        const setTmpReorder = propSetTmpReorder !== undefined ? propSetTmpReorder : internalSetTmpReorder;

        // Use propGridKey/propSetGridKey if provided, otherwise use internal state
        const [internalGridKey, internalSetGridKey] = useState(0);
        const gridKey = propGridKey !== undefined ? propGridKey : internalGridKey;
        const setGridKey = propSetGridKey !== undefined ? propSetGridKey : internalSetGridKey;

        // Use propSelectedState/propSetSelectedState if provided, otherwise use internal state
        const [internalSelectedState, internalSetSelectedState] = useState<{ [id: string]: boolean | number[] }>({});
        const selectedState = propSelectedState !== undefined ? propSelectedState : internalSelectedState;
        const setSelectedState = propSetSelectedState !== undefined ? propSetSelectedState : internalSetSelectedState;

        // Use propCurrentColumns/propSetCurrentColumns if provided, otherwise use internal state
        const [internalCurrentColumns, internalSetCurrentColumns] = useState<GridColumnProps[]>(() =>
            updateColumnsFields(columns, rowReorder, orderIndexColumn, []),
        );
        const currentColumns = propCurrentColumns !== undefined ? propCurrentColumns : internalCurrentColumns;
        const setCurrentColumns =
            propSetCurrentColumns !== undefined ? propSetCurrentColumns : internalSetCurrentColumns;

        // Use propSaveTableState if provided, otherwise use internal state
        const internalSaveTableState = useRef(false);
        const saveTableState = propSaveTableState !== undefined ? propSaveTableState : internalSaveTableState;

        const [columnsState, setColumnsState] = React.useState<GridColumnState[]>([]);

        const saveStateToLocalStorage = (columnsStateToSave?: GridColumnState[]) => {
            const state = {
                columnsState: columnsStateToSave ?? columnsState,
            };
            saveTableState.current = false;
            onStateChange?.(JSON.stringify(state));
        };

        const flattenColumns = (cols: GridColumnProps[]): GridColumnProps[] =>
            cols.flatMap((col) => (col.children?.length ? [col, ...flattenColumns(col.children)] : [col]));

        // Kendo reconciles `locked` from the columnsState prop by POSITIONAL index among visible
        // siblings at each depth (see syncLockedColumns in @progress/kendo-react-grid), not by id.
        // Our columnsState array keeps hidden columns in place, so as soon as any column is hidden,
        // Kendo's visible-only index no longer lines up with our array and a column can silently
        // inherit an unrelated sibling's locked state. We already drive `locked` correctly via the
        // `locked` prop on each <GridColumn> (from currentColumns), so strip it from the array we hand
        // to Kendo's columnsState prop to stop it from being used as a (buggy) fallback. It's kept
        // intact in our own columnsState state/localStorage, which is restored by id, not position.
        const stripLockedForKendo = (state: GridColumnState[]): GridColumnState[] =>
            state.map((col) => ({
                ...col,
                locked: undefined,
                children: col.children ? stripLockedForKendo(col.children) : undefined,
            }));

        // Kendo's own columnsState events (e.g. after a column reorder) don't reliably carry the
        // `hidden` flag for every column, so re-apply visibility from `currentColumns` (our source
        // of truth for show/hide) whenever we accept a new columnsState. `locked` needs the same
        // treatment: since we strip `locked` out of the columnsState prop we hand to Kendo (see
        // stripLockedForKendo above), Kendo's own columnsState events report `locked: undefined` for
        // every column, so trusting `col.locked` here would clear every column's locked state on
        // every reorder/resize/etc.
        const syncHiddenState = (state: GridColumnState[]): GridColumnState[] => {
            const currentColumnsMap = new Map(flattenColumns(currentColumns).map((col) => [col.id, col]));
            const updateHidden = (col: GridColumnState): GridColumnState => {
                const currentCol = currentColumnsMap.get(col.id);
                return {
                    ...col,
                    hidden: currentCol ? !currentCol.show : col.hidden,
                    children: col.children?.map(updateHidden),
                    locked: currentCol ? !!currentCol.locked : (col.locked ?? false),
                };
            };
            return state.map(updateHidden);
        };

        const getColumnMinWidth = (column: GridColumnProps): number | undefined => {
            const childrenMinWidthTotal = column.children
                ?.filter((col) => col.show)
                ?.reduce((n, { minWidth, width }) => n + (minWidth ?? (width != null ? Number(width) : 0)), 0);
            return (
                childrenMinWidthTotal || (column.minWidth ?? (column.width != null ? Number(column.width) : undefined))
            );
        };

        // Builds a GridColumnState tree from our own column config, recursing into children so nested
        // (grouped) columns carry their own locked/hidden state instead of losing it a level down.
        const toColumnState = (column: GridColumnProps, index: number): GridColumnState => ({
            id: column.id ?? '',
            field: column.field,
            hidden: !column.show,
            locked: column.locked ?? false,
            orderIndex: index,
            maxWidth: column.maxWidth,
            minWidth: column.minWidth ?? (column.width != null ? Number(column.width) : undefined),
            title: column.title,
            children: column.children?.map(toColumnState),
        });

        const hydrateFilterOperators = (filterList: CompositeFilterDescriptor) => {
            const mappedFilters = filterList.filters.map((curFilter) => {
                if ((curFilter as FilterDescriptor) && 'field' in curFilter) {
                    const column = columns.find((col) => col.field === curFilter.field);
                    if (!column) return curFilter;
                    const value =
                        typeof curFilter.value === 'object' && 'search' in curFilter.value
                            ? curFilter.value.search
                            : null;
                    return {
                        ...curFilter,
                        operator:
                            value !== null && value !== ''
                                ? calcOperator(
                                      {
                                          value:
                                              typeof value === 'boolean' ||
                                              column?.filter === 'numeric' ||
                                              (column.lovData && column.lovData.length > 0 && !column.lovFilterContains)
                                                  ? FilterOperators.Equal
                                                  : FilterOperators.Contains,
                                          label: '',
                                      },
                                      column.filter,
                                      column.filterKey,
                                      column.comparisonFilterKey,
                                  )
                                : '',
                    };
                }
                return curFilter;
            });
            if (mappedFilters) return { ...filterList, filters: mappedFilters };
            return filterList;
        };

        const onColumnsStateChange = (event: GridColumnsStateChangeEvent) => {
            saveTableState.current = true;
            const syncedColumnsState = syncHiddenState(event.columnsState);
            setColumnsState(syncedColumnsState);
            onColumnsStateChangeProp?.(syncedColumnsState);
        };

        const handleColumnResize = (event: GridColumnResizeEvent) => {
            if (!event.end || !persistentStorageKey) return;
            const widthById = new Map(event.columns.map((c) => [c.id, c.width]));
            saveTableState.current = true;
            setColumnsState((prev) => prev.map((col) => ({ ...col, width: widthById.get(col.id) ?? col.width })));
        };

        useEffect(() => {
            if (externalColumnsState === undefined) return;
            setGridKey((prev) => prev + 1);
            setColumnsState(externalColumnsState);
            onColumnsStateChangeProp?.(externalColumnsState);
            setCurrentColumns(updateColumnsFields(columns, rowReorder, orderIndexColumn, externalColumnsState));
        }, [externalColumnsState]);

        useEffect(() => {
            filterActive.setValue(!!filter?.filters.length);
        }, [filter]);

        const gridRef = useRef<GridHandle & GridRefExtension>(null);
        const gridSelectableItemCountRef = useRef<number>(0);
        const [applyMinWidth, setApplyMinWidth] = React.useState(false);
        const [gridCurrent, setGridCurrent] = React.useState(0);
        const [minGridWidth, setMinGridWidth] = useState(0);
        const activeItem = useRef<unknown>(null);
        const gridContainerRef = useRef<HTMLElement | null>(null);
        const idGetter = getter(dataItemKey ?? '');

        useImperativeHandle(ref, () => ({
            unselectRow(primaryKey: string) {
                if (!selectable || !dataItemKey || !selectedState[primaryKey]) return;
                const updatedState = {
                    ...selectedState,
                    [primaryKey]: false,
                };
                setSelectedState(updatedState);
                const datasource = Array.isArray(data) ? data : data?.data;
                const dataItem = datasource?.find((x) => idGetter(x) === primaryKey) as unknown;
                const event = {
                    startDataItem: dataItem,
                    startRowIndex: 0,
                    endRowIndex: 0,
                    dataItems: datasource,
                };
                onSelectionChange && onSelectionChange(event as GridSelectionChangeEvent, updatedState);
            },
            validateSelection(newData: unknown[]) {
                if (!selectable || !dataItemKey) return;

                const selectedRows = Object.keys(selectedState).reduce<string[]>((prev, current) => {
                    if (selectedState[current]) prev.push(current);
                    return prev;
                }, []);

                // No selected rows
                if (!selectedRows.length) return;

                // Validate the selected rows are still in the datasource
                const missingRows: string[] = [];
                selectedRows.forEach((key) => {
                    const dataItem = newData?.find(
                        (x) =>
                            (
                                idGetter(x) as number | string | Date | boolean | object | null | undefined
                            )?.toString() === key,
                    );
                    if (!dataItem) missingRows.push(key);
                });

                // All rows accounted for
                if (!missingRows.length) return;

                // Previously selected rows are no longer part of the datasource
                const updatedState = { ...selectedState };
                missingRows.forEach((key) => {
                    delete updatedState[key];
                });
                setSelectedState(updatedState);

                const event = {
                    startRowIndex: 0,
                    endRowIndex: 0,
                    dataItems: newData,
                };
                onSelectionChange && onSelectionChange(event as GridSelectionChangeEvent, updatedState);
            },
            clearSelectedRows: () => {
                setSelectedState({});
                const event = {
                    startRowIndex: 0,
                    endRowIndex: 0,
                    dataItems: data,
                };
                onSelectionChange && onSelectionChange(event as GridSelectionChangeEvent, {});
            },
        }));

        useEffect(() => {
            if (saveTableState.current && persistentStorageKey) saveStateToLocalStorage();
        }, [columnsState]);

        useEffect(() => {
            if (gridRef.current && gridRef.current.element) {
                gridContainerRef.current = gridRef.current.element.querySelector('.k-grid-content');
            }
        }, [gridRef.current]);

        useEffect(() => {
            if (clearGridSelectedRows) setSelectedState({});
        }, [clearGridSelectedRows]);

        const isVirtualScrollWithRemoteData = () => {
            return apiGrid && scrollable === 'virtual' && rest.pageSize !== undefined && rest.skip !== undefined;
        };

        const headerSelectionChange = (event: GridHeaderSelectionChangeEvent) => {
            if (selectionDisabledField) {
                const firstItem = event.dataItems[0];
                const hasProp = Object.hasOwn(firstItem, selectionDisabledField);
                if (hasProp) {
                    const checkboxElement = event.syntheticEvent.target as HTMLInputElement;
                    const { checked } = checkboxElement;
                    const newSelectedState: SelectDescriptor = {};
                    event.dataItems.forEach((item) => {
                        if (!item[selectionDisabledField]) {
                            newSelectedState[idGetter(item)] = checked;
                        }
                    });
                    setSelectedState(newSelectedState);
                    onHeaderSelectionChange && onHeaderSelectionChange(event);
                    return;
                }
            }
            if (dataItemKey) {
                setSelectedState(event.select);
            }
            onHeaderSelectionChange && onHeaderSelectionChange(event);
        };

        const handleFilterChange = async (event: GridFilterChangeEvent) => {
            if (persistentStorageKey) {
                const newFilter: CompositeFilterDescriptor = event.filter
                    ? { ...event.filter }
                    : { logic: 'and', filters: [] };

                setDataState((prevState) => ({
                    ...prevState,
                    filter: newFilter,
                }));
            }

            if (onFilterChange) await onFilterChange(event);
        };

        const handleSortChange = (event: GridSortChangeEvent) => {
            setDataState((prevState) => ({
                ...prevState,
                sort: event.sort,
            }));
            onSortChange && onSortChange(event);
        };

        const handleSelectionChange = async (event: GridSelectionChangeEvent) => {
            if ((rowMenuActions && event.startColIndex === 0) || readOnly) return;

            if (checkBoxSelectionOnly && !event.syntheticEvent) return;

            let updatedEvent = event;
            // The event dataItems will only be the grid `data` prop, which could be sliced
            // If that's the case, replace the dataItems prop with the full `data` prop, so that users
            // can correctly multi-select across "paginated" data
            // The startDataItem is also unreliable when the grid is virtualized, kendo is somehow getting it wrong
            // after the grid has scrolled through page of data, it appears to be selecting the data at the
            // startRowIndex + the grid skip. Fortunately the startRowIndex always seems to be right,
            // replace the startDataItem with the item at the startRowIndex position in the data array
            if (isVirtualScrollWithRemoteData()) {
                const dataItems = data ? (Array.isArray(data) ? data : data.data) : [];
                updatedEvent = {
                    ...event,
                    dataItems,
                    startDataItem: dataItems[updatedEvent.startRowIndex],
                };
            }

            const proceed = (await preSelectionChange?.(event)) ?? true;
            if (!proceed) return;
            let newSelectedState = selectedState;
            if (dataItemKey) {
                newSelectedState = getSelectedState({
                    event: updatedEvent,
                    selectedState,
                    dataItemKey,
                });
                setSelectedState(newSelectedState);
            }
            // Get the Data Items for the selected rows instead??
            onSelectionChange && onSelectionChange(updatedEvent, newSelectedState);
        };

        const updateReorderedColumns = (
            prevColumns: GridColumnProps[],
            newColumns: GridColumnProps[],
        ): GridColumnProps[] => {
            return prevColumns.map((col) => {
                const newColProps = newColumns.find((c) => c.id === col.id);
                if (newColProps) {
                    // Don't copy cells prop forward, always use original (i.e. what was passed on props)
                    // otherwise this breaks, as it renders the custom cells using the state of the columns
                    // when the grid was initialized, thus doesn't use new state (i.e. locked)

                    // Similarly, dont copy minWidth prop forward, always use original
                    const updatedCol = {
                        ...newColProps,
                        cells: col.cells,
                        minWidth: col.minWidth,
                    };

                    if (col.children && newColProps.children) {
                        updatedCol.children = updateReorderedColumns(col.children, newColProps.children);
                    }
                    return updatedCol;
                }
                if (col.children) {
                    return { ...col, children: updateReorderedColumns(col.children, newColumns) };
                }
                return { ...col };
            });
        };

        const fullColumnReorder = (event: GridColumnReorderEvent) => {
            onColumnReorder && onColumnReorder(event);
            const updatedColumns = updateReorderedColumns(currentColumns, event.columns as GridColumnProps[]);
            setCurrentColumns(updatedColumns);
        };

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const applyHighlightSort = (processedData: any[] | DataResult | null | undefined) => {
            if (!rest.highlight || Object.keys(rest.highlight).length === 0 || !Array.isArray(processedData))
                return processedData;

            return [...processedData].sort((a, b) => {
                const aVal = (a as unknown as { [k in string]: never })[dataItemKey ?? ''] as string;
                const bVal = (b as unknown as { [k in string]: never })[dataItemKey ?? ''] as string;
                if (
                    Object.keys(rest.highlight ?? {}).includes(aVal.toString()) &&
                    !Object.keys(rest.highlight ?? {}).includes(bVal.toString())
                )
                    return -1;
                if (
                    Object.keys(rest.highlight ?? {}).includes(bVal.toString()) &&
                    !Object.keys(rest.highlight ?? {}).includes(aVal.toString())
                )
                    return 1;

                return 0;
            });
        };

        const mapDataForSelect = () => {
            const sliceData = isVirtualScrollWithRemoteData();

            if (data) {
                if (Array.isArray(data) && !sliceData) {
                    if (selectionDisabledField && data.length > 0) {
                        const firstItem = data[0];
                        const hasProp = Object.hasOwn(firstItem, selectionDisabledField);
                        if (hasProp) {
                            gridSelectableItemCountRef.current = data.filter(
                                (item) => !item[selectionDisabledField],
                            ).length;
                        }
                    } else gridSelectableItemCountRef.current = data.length;
                } else if (Array.isArray(data)) {
                    gridSelectableItemCountRef.current = rest.pageSize ?? 0;
                } else gridSelectableItemCountRef.current = data.data.length;
            }

            const canSelect = selectable && dataItemKey;
            if ((!sliceData && !canSelect) || !data) {
                return applyHighlightSort(data);
            }

            // eslint-disable-next-line
            const updatedSelectedField = (array: any[]) => {
                let transformedData = array;
                if (sliceData) transformedData = transformedData.slice(rest.skip, rest.skip! + rest.pageSize!);
                if (canSelect)
                    transformedData = transformedData.map<unknown>((x) => {
                        if (x)
                            return {
                                ...x,
                                [selectedField || SELECTED_FIELD]: selectedState[idGetter(x)],
                            };
                        return x;
                    });

                return transformedData as unknown[];
            };
            if (Array.isArray(data)) {
                return updatedSelectedField(data);
            }

            return {
                ...data,
                data: updatedSelectedField(data.data),
            };
        };

        const reorder = (dataItem: unknown, direction: 'before' | 'after' | null) => {
            if (activeItem.current === dataItem || direction === null) return; // No change if dropping in the same spot
            setReorderIsDirty && setReorderIsDirty();
            setComponentHasChanges(GRID_COMPONENT_NAME, true);

            const reorderedData = tmpReorder && Array.isArray(tmpReorder) ? [...tmpReorder] : []; // Create a copy of the data array
            // Locate the index of the item currently being dragged
            const prevIndex = reorderedData.findIndex((p) => p === activeItem.current);
            if (prevIndex === -1) return; // Exit if the active item isn't found

            // Determine the index where the active item should be placed
            let nextIndex = reorderedData.findIndex((p) => p === dataItem) + (direction === 'before' ? -1 : 0);

            if (prevIndex > nextIndex) nextIndex++;

            // Move the active item to the new position
            reorderedData.splice(prevIndex, 1); // Remove from the old position
            reorderedData.splice(nextIndex, 0, activeItem.current); // Insert at the new position

            setTmpReorder(reorderedData); // Update the grid data state
            activeItem.current = null; // Clear the active item after drop
        };

        // Sets the item being dragged as the active item
        const dragStart = (dataItem: unknown) => {
            activeItem.current = dataItem;
        };

        const contextValue = useMemo(() => {
            return { dragStart, reorder };
        }, [tmpReorder, activeItem.current]);

        const [gridWidth, setGridWidth] = useState<number>();
        const [gridHeight, setGridHeight] = useState<number>();

        const updateGridSize = () => {
            if (cardRef?.current) {
                setGridWidth(cardRef.current.element?.offsetWidth ?? undefined);
                setGridHeight(cardRef.current.element?.offsetHeight ?? undefined);
            }
        };

        useEffect(() => {
            const element = cardRef?.current?.element;
            if (!element) return;

            const observer = new ResizeObserver(() => {
                updateGridSize();
            });

            observer.observe(element);

            return () => observer.disconnect();
        }, []);

        const handleResize = useCallback(
            debounce(() => {
                updateGridSize();

                const gridItem = gridContainerRef.current;
                if (gridItem) {
                    const gridOffsetWidth = gridItem.offsetWidth;
                    setGridCurrent(gridOffsetWidth - ADJUST_GRID_CONTENT_PADDING);
                    if (gridOffsetWidth < minGridWidth) {
                        setApplyMinWidth(true);
                    } else if (gridOffsetWidth > minGridWidth) {
                        setApplyMinWidth(false);
                    }

                    if (scrollable === 'virtual' && apiGrid && onPageChange) {
                        // Changing the key forces a re-draw, meaning the scroll will be reset to the top
                        // Reset the page by emitting a onPageChange event for the first page
                        onPageChange({
                            page: {
                                skip: 0,
                                take: rest.pageSize!,
                            },
                        } as GridPageChangeEvent);
                    }
                    // Re-renders the grid when the browser window is resized. This ensures that the left style attribute
                    // is recalculated correctly for all locked columns.
                    setGridKey((prev) => prev + 1);
                }
            }, 200),
            [minGridWidth],
        );

        useEffect(() => {
            const observer = new ResizeObserver((entries) => {
                if (entries.length && gridContainerRef.current) {
                    const gridItem = gridContainerRef.current;
                    if (gridItem) {
                        // Only trigger the resize if the width diff is greater than a scrollbar.
                        // This helps with expanding/collapsing the toolkit, as that adds a scrollbar
                        // but removes it at half or ful height. Triggering the resize during expand/collapse results in
                        // the grid redrawing and freezing the expand/collapse of the toolkit while it redraws
                        const handleWidthResize =
                            Math.abs(gridItem.offsetWidth - gridCurrent - ADJUST_GRID_CONTENT_PADDING) >
                            SCROLL_BAR_WIDTH;

                        // Only worry about height changes if the grid is virtual scrollable and the height has changed
                        const handleHeightChange =
                            scrollable === 'virtual' &&
                            cardRef?.current?.element &&
                            gridHeight !== cardRef?.current.element.offsetHeight;

                        if (handleWidthResize || handleHeightChange) handleResize();
                    }
                }
            });
            if (gridContainerRef.current) observer.observe(gridContainerRef.current);
            if (scrollGridRight && gridContainerRef.current)
                gridContainerRef.current.scrollLeft = gridContainerRef.current?.scrollWidth;
            return () => observer.disconnect();
        }, [gridKey]);

        const getRowMenuActionWidth = () => {
            if (!rowMenuActions || !rowMenuActions.length) return 0;
            return rowMenuActions.length === 2 ? 50 : 30;
        };

        const countResizableChildCols = (col: GridColumnProps) => {
            let sum = 0;
            if (col.children && col.children.length > 0) {
                col.children.forEach((c) => {
                    sum += countResizableChildCols(c);
                });
            } else if (col.show && !col.customColumn) {
                sum = 1;
            }

            return sum;
        };

        const setWidth = (minWidth?: number | undefined, isCustom?: boolean | undefined) => {
            if (minWidth === undefined) {
                minWidth = 0;
            }

            if (isCustom) {
                return minWidth;
            }

            let resizableCols = 0;
            currentColumns.forEach((col) => {
                if (col.children && col.children.length > 0)
                    col.children.forEach((c) => {
                        resizableCols += countResizableChildCols(c);
                    });
                else if (col.show && !col.customColumn && col.columnType !== 'checkbox') resizableCols += 1;
            });

            return applyMinWidth
                ? minWidth
                : minWidth + (gridCurrent - minGridWidth - getRowMenuActionWidth()) / resizableCols;
        };

        const buildColumnState = (column: GridColumnProps, index: number): GridColumnState => {
            const isCustom = !!(column.customColumn || column.columnType === 'checkbox' || column.lockWidth);
            return {
                ...toColumnState(column, index),
                width: setWidth(getColumnMinWidth(column), isCustom),
                children: column.children?.map((child, childIndex) => buildColumnState(child, childIndex)),
            };
        };

        useEffect(() => {
            if (!propHiddenColumnsUpdated?.value || !persistentStorageKey) return;
            setGridKey((prev) => prev + 1);

            const newColumnsState = currentColumns.map((col, i) => buildColumnState(col, i));

            const updatedColumnsState = syncHiddenState(newColumnsState);
            setColumnsState(updatedColumnsState);
            saveStateToLocalStorage(updatedColumnsState);
            propHiddenColumnsUpdated.setFalse();
        }, [propHiddenColumnsUpdated]);

        const handleRowActionClick = (action: RowMenuAction, dataItem: unknown) => {
            // We set the selected state to the row that was clicked
            const newId = idGetter(dataItem);
            const datasource = Array.isArray(data) ? data : data?.data;
            const selected = { [newId]: true };
            setSelectedState(selected);
            const event = {
                startDataItem: dataItem,
                startRowIndex: 0,
                endRowIndex: 0,
                dataItems: datasource,
            };
            onSelectionChange && onSelectionChange(event as GridSelectionChangeEvent, selected);

            action.onClick(dataItem);
        };

        useEffect(() => {
            setExportColumns && setExportColumns(currentColumns);

            const minWidth = getMinGridWidth(currentColumns);
            setMinGridWidth(minWidth);

            if (gridContainerRef.current) {
                setApplyMinWidth(gridContainerRef.current.offsetWidth < minWidth);
            }
        }, [currentColumns]);

        useLayoutEffect(() => {
            // Re-renders the grid when the locked column configuration changes using the grid settings menu. This ensures
            // that the left style attribute is calculated correctly for all locked columns.
            setGridKey((prev) => prev + 1);
            lockedColumnsUpdated.setFalse();

            if (persistentStorageKey && lockedColumnsUpdated?.value) {
                // Always recompute widths here rather than reusing an existing (non-empty) columnsState:
                // a previously-saved layout's widths were computed for a different lock configuration, so
                // reusing them verbatim can leave a stale/incorrect width baked into the state indefinitely.
                const newColumnsState = currentColumns.map((col, i) => buildColumnState(col, i));

                // syncHiddenState reconciles `locked` (and `hidden`) from `currentColumns` recursively,
                // by id, at every depth — needed here so a locked/unlocked group's children carry the
                // change too, not just the group column itself.
                const updatedColumnsState = syncHiddenState(newColumnsState);

                setColumnsState(updatedColumnsState);
                saveStateToLocalStorage(updatedColumnsState);
            }

            if (scrollable === 'virtual' && apiGrid && onPageChange) {
                onPageChange({
                    page: {
                        skip: 0,
                        take: rest.pageSize!,
                    },
                } as GridPageChangeEvent);
            }
        }, [lockedColumnsUpdated]);

        useEffect(() => {
            setCurrentColumns((state) => {
                return state.map((s) => ({
                    ...s,
                    locked: reorderActive.value ? false : s.lockedByDefault,
                    show:
                        s.id === REORDER_COLUMN_ID
                            ? reorderActive.value
                            : s.id === ORDER_COLUMN_ID || s.id === ROW_MENU_ACTION_COLUMN_ID
                              ? !reorderActive.value
                              : s.show,
                }));
            });
            // Reset the grid component changes tracking that gets set on row re-order
            if (!reorderActive.value) setComponentHasChanges(GRID_COMPONENT_NAME, false);
        }, [reorderActive.value]);

        // We need to track changes to the data from the parent. This should only really happen when the FE grid resets the
        // sort/filter when enabling reorder.
        useEffect(() => {
            setCurrentColumns(updateColumnsFields(columns, rowReorder, orderIndexColumn, columnsState));
        }, [columns]);

        const forceScrollTo = useRef<number | null>(null);

        const selectDefaultRow = () => {
            if (defaultSelectedItem && dataItemKey) {
                if (gridRef.current && data) {
                    const newId = idGetter(defaultSelectedItem);
                    const datasource = Array.isArray(data) ? data : data?.data;
                    const index = datasource.findIndex((x) => idGetter(x) === newId);
                    const selected = { [newId]: true };
                    setSelectedState(selected);
                    const event = {
                        startDataItem: defaultSelectedItem,
                        startRowIndex: index,
                        endRowIndex: index,
                        dataItems: datasource,
                    };
                    onSelectionChange && onSelectionChange(event as GridSelectionChangeEvent, selected);
                    // Find the row index
                    if (index > -1 && !preventAutoscroll) {
                        // preventAutoscroll can be used to prevent auto scroll when the unsaved changes warning dialog
                        // fires after a grid row change.
                        // Sometimes the grid is scrolling to a newly added record before the data has fully rendered which leads
                        // to a blank grid being displayed but at the correct scroll position, this setTimeout resolves
                        // that issue.
                        // This timeout was actually causing some grids not to scroll, increased the timeout from 0 to 1
                        setTimeout(() => {
                            gridRef.current?.scrollIntoView({ rowIndex: index });
                        }, 0);

                        forceScrollTo.current = scrollable === 'virtual' ? index : null;
                    }
                }
            }
        };

        useEffect(() => {
            selectDefaultRow();
        }, [defaultSelectedItem]);

        useEffect(() => {
            if (clientGrid) selectDefaultRow();
        }, [data]);

        // I hate this.
        // Some of our virtual scroll grids do not have a true fixed row height. Eg Study Observations.
        // They can be 30 or 40px tall depending on content. This difference can mess with the kendo scrollIntoView.
        // Primarily (I believe) because it will scroll the row into view while it's a loader (which will be 30px), but the
        // loaded content could be different.
        // This will fire every render, if the ref is set and the row is not a skeleton, it will scroll the row into view.
        // An option that might work is to put `loading` as a dependency, and then use a setTimouet to scroll, assuming at
        // that point the DOM will have painted not just the row, but the loading ones too.
        useEffect(() => {
            if (forceScrollTo.current != null && gridRef.current?.element) {
                const tr = gridRef.current.element.querySelector(
                    `tbody > tr[absolute-row-index='${forceScrollTo.current}']`,
                );
                if (tr) {
                    const hasSkeletons = gridRef.current.element.querySelector('.k-skeleton');
                    if (!hasSkeletons) {
                        tr.scrollIntoView();
                        forceScrollTo.current = null;
                    }
                }
            }
        });

        useEffect(() => {
            const minWidth = getMinGridWidth(currentColumns);
            if (gridContainerRef.current) {
                setGridCurrent(gridContainerRef.current.offsetWidth - ADJUST_GRID_CONTENT_PADDING);
                setApplyMinWidth(gridContainerRef.current.offsetWidth < minWidth);
                if (scrollGridRight) gridContainerRef.current.scrollLeft = gridContainerRef.current.scrollWidth;
            }
        }, []);

        const CustomRow = (props: GridCustomRowProps) => {
            const { trProps, children, absoluteRowIndex } = props;
            const trCustomerProps: Record<string, string | number> = {
                'data-grid-row-index': absoluteRowIndex,
                'data-testid': GRID_ROW_TEST_ID_MASTER,
            };

            return (
                <tr
                    {...trProps}
                    {...trCustomerProps}
                    className={cn(trProps?.className, props?.isAltRow ? 'k-alt' : '')}
                >
                    {children}
                </tr>
            );
        };

        // FilterCustomCell emits operator:'' for non-clientGrid grids; Kendo's process() crashes on
        // that. Hydrate the operators (converts '' → calcOperator function) and apply them manually.
        const applyHydratedFilter = (items: unknown[], descriptor: CompositeFilterDescriptor): unknown[] => {
            const hydrated = hydrateFilterOperators(descriptor);
            const isOr = hydrated.logic === 'or';
            return items.filter((item) => {
                const results = hydrated.filters.map((f) => {
                    if (!('field' in f) || typeof (f as FilterDescriptor).operator !== 'function') return true;
                    const desc = f as FilterDescriptor;
                    const fieldValue = getter(desc.field as string)(item as Record<string, unknown>);
                    return ((desc.operator as (a: unknown, b: unknown) => unknown)(fieldValue, desc.value) ??
                        true) as boolean;
                });
                return isOr ? results.some(Boolean) : results.every(Boolean);
            });
        };

        const processedData = persistentStorageKey
            ? (() => {
                  const raw = Array.isArray(data) ? data : data?.data ? data.data : [];
                  const filtered = dataState.filter?.filters?.length ? applyHydratedFilter(raw, dataState.filter) : raw;
                  return process(filtered, { sort: dataState.sort });
              })()
            : mapDataForSelect();

        const grid = (
            <ReorderContext.Provider value={contextValue}>
                <DragAndDrop key={gridKey}>
                    <KendoGrid
                        ref={gridRef}
                        data={tmpReorder ?? processedData}
                        reorderable={readOnly ? false : reorderable && !reorderActive.value}
                        filterable={filterable && !reorderActive.value}
                        sortable={readOnly ? false : sortable && !reorderActive.value}
                        resizable={readOnly ? false : resizable}
                        scrollable={scrollable}
                        className={cn('core-grid k-h-full', className)}
                        style={{
                            ...style,
                            width: rest.columnVirtualization
                                ? gridWidth
                                    ? `${gridWidth}px`
                                    : style?.width
                                : style?.width,
                            height:
                                scrollable === 'virtual'
                                    ? gridHeight
                                        ? `calc(${gridHeight}px - 33px)`
                                        : style?.width
                                    : `calc(${maxHeight}px - ${GRID_HEADER_HEIGHT}px)`,
                        }}
                        onColumnReorder={fullColumnReorder}
                        onColumnResize={handleColumnResize}
                        dataItemKey={dataItemKey}
                        defaultSelect={defaultSelect}
                        onSelectionChange={handleSelectionChange}
                        onHeaderSelectionChange={headerSelectionChange}
                        selectable={
                            reorderActive.value || (readOnly && scrollable !== 'virtual') ? undefined : selectable
                        }
                        filter={persistentStorageKey ? dataState.filter : filter}
                        onFilterChange={handleFilterChange}
                        onSortChange={handleSortChange}
                        onPageChange={onPageChange}
                        select={selectedState}
                        {...rest}
                        sort={dataState.sort}
                        columnsState={persistentStorageKey ? stripLockedForKendo(columnsState) : undefined}
                        onColumnsStateChange={onColumnsStateChange}
                        rows={{
                            data: reorderActive.value
                                ? (rowProps) => {
                                      return <DraggableRow {...rowProps} />;
                                  }
                                : CustomRow,
                        }}
                    >
                        {rowMenuActions &&
                            renderGridColumn(
                                [
                                    {
                                        ...rowMenuActionColumn,
                                        orderIndex: -3,
                                        locked: true,
                                        disableReorder: true,
                                        show: !reorderActive.value,
                                        showByDefault: true,
                                        children: undefined,
                                        width:
                                            totalCount || clientGrid
                                                ? rowMenuActionsWidth || (rowMenuActions.length === 2 ? 50 : 30)
                                                : undefined,
                                        customColumn: true,
                                        cells: {
                                            data: (props) => (
                                                <MenuActionCell
                                                    {...props}
                                                    actions={rowMenuActions.map((action): RowMenuAction => {
                                                        return {
                                                            ...action,
                                                            onClick: (dataItem) =>
                                                                handleRowActionClick(action, dataItem),
                                                        };
                                                    })}
                                                    excludeHiddenRowMenuActions={excludeHiddenRowMenuActions}
                                                />
                                            ),
                                            headerCell: (props) => <HeaderCell {...props}>{props.children}</HeaderCell>,
                                        },
                                    },
                                ],
                                reorderActive.value,
                                gridSelectableItemCountRef.current,
                                undefined,
                                setWidth,
                                clientGrid,
                                showPlaceholder,
                                selectedState,
                            )}
                        {renderGridColumn(
                            currentColumns,
                            reorderActive.value,
                            gridSelectableItemCountRef.current,
                            undefined,
                            setWidth,
                            clientGrid,
                            showPlaceholder,
                            selectedState,
                        )}
                        <GridNoRecords>
                            {!loading && (
                                <NoRecordsMessage
                                    message={
                                        titleIsEmpty
                                            ? (noTitleMessage ?? t('grid.emptyTableHeader'))
                                            : (noTitleMessage ?? t('grid.noRecordsAvailable'))
                                    }
                                    className="k-w-full"
                                    onGrid
                                />
                            )}
                            {loading && ''}
                        </GridNoRecords>
                    </KendoGrid>
                </DragAndDrop>
            </ReorderContext.Provider>
        );

        const warningDialogConfirmation = async () => {
            if (showAddWarningDialog.value) {
                if (clearFilter) {
                    const proceed = await clearFilter();
                    if (proceed === false) {
                        showAddWarningDialog.setFalse();
                        return;
                    }
                }
                if (persistentStorageKey) {
                    setDataState((prevState) => ({ ...prevState, filter: { logic: 'and', filters: [] } }));
                }
                setGridKey((prev) => prev + 1);
                addCallback && addCallback();
                showAddWarningDialog.setFalse();
            }
            if (!!editWarningCallback) {
                editWarningCallback();
                setEditWarningCallback(undefined);
            }
        };

        return (
            <>
                {(showAddWarningDialog.value || editWarningCallback !== undefined) && (
                    <ConfirmationDialog
                        dataTestId="confirm-add-study-observation"
                        title={t('grid.filterSortWarning.title')}
                        onSubmit={warningDialogConfirmation}
                        onClose={() => {
                            showAddWarningDialog.setFalse();
                            setEditWarningCallback(undefined);
                        }}
                        primaryBtnLabel={t('common:continue')}
                    >
                        {t('grid.filterSortWarning.body')}
                    </ConfirmationDialog>
                )}
                {grid}

                {useLoadingOverlay ? (
                    <div className={`loading-overlay ${!loading ? 'fade-out' : ''}`}>
                        <Loading />
                    </div>
                ) : (
                    <>{loading && <Loader position={LoaderPosition.bottom} />}</>
                )}
            </>
        );
    },
);

export default Grid;
